import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SendIcon from "@mui/icons-material/Send";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate, useOutletContext } from "react-router-dom";
import InternalNoteExpandedEditor from "../../components/MessageHistory/InternalNoteExpandedEditor";
import {
  composerHasTextPayload,
  composerPlainText,
  isProbablyHtml,
  stripHtmlToPlain,
} from "../../components/MessageHistory/composerHtmlUtils";
import { portalCreateTicket } from "../../api/clientPortal";
import { getClientPortalErrorMessage } from "./clientPortalErrorMessage";

const MESSAGE_MAX_LENGTH = 8000;

const TIPS = [
  "Explique o que aconteceu e quando começou.",
  "Informe se já tentou alguma solução.",
  "Inclua números de pedido, protocolo ou referências, se tiver.",
];

export default function ClientPortalNewTicketPage() {
  const { q } = useOutletContext();
  const navigate = useNavigate();
  const theme = useTheme();

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEditorOpen, setExpandedEditorOpen] = useState(false);

  const compactMessage = isProbablyHtml(message)
    ? stripHtmlToPlain(message, { trim: false })
    : message;
  const plainLength = composerPlainText(message, { trim: false }).length;
  const canSubmit = composerHasTextPayload(message) && plainLength <= MESSAGE_MAX_LENGTH;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const body = composerPlainText(message, { trim: true });
    if (!body) {
      setError("Descreva sua solicitação.");
      return;
    }
    if (body.length > MESSAGE_MAX_LENGTH) {
      setError(`A descrição pode ter no máximo ${MESSAGE_MAX_LENGTH} caracteres.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await portalCreateTicket({
        subject: subject.trim(),
        message: body,
      });
      const newId = res?.id;
      navigate(newId ? `/portal-cliente/app/${newId}${q}` : `/portal-cliente/app${q}`);
    } catch (err) {
      const status = err?.response?.status;
      const payload = err?.response?.data?.data || err?.response?.data;
      const existingId = payload?.id;

      if (status === 409 && existingId) {
        navigate(`/portal-cliente/app/${existingId}${q}`);
        return;
      }

      setError(getClientPortalErrorMessage(err, { context: "newTicket" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 3, sm: 4 } }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
        <Tooltip title="Voltar">
          <IconButton
            size="small"
            onClick={() => navigate(`/portal-cliente/app${q}`)}
            aria-label="Voltar"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1" fontWeight={700}>
          Nova solicitação
        </Typography>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          overflow: "hidden",
          bgcolor: "background.paper",
          boxShadow: `0 8px 28px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
              }}
            >
              <SupportAgentOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Como podemos ajudar?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Descreva seu problema ou dúvida. Nossa equipe responderá nesta conversa assim que possível.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2.25}>
            <TextField
              label="Assunto"
              placeholder="Ex.: Dúvida sobre cobrança, problema no pedido…"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              size="small"
              helperText="Opcional — ajuda a equipe a entender o tema rapidamente."
              inputProps={{ maxLength: 200 }}
            />

            <Box>
              <TextField
                label="Descreva sua solicitação"
                placeholder="Conte o que aconteceu com o máximo de detalhes que puder…"
                value={compactMessage}
                onChange={(e) => setMessage(e.target.value)}
                required
                multiline
                minRows={6}
                maxRows={12}
                fullWidth
                error={plainLength > MESSAGE_MAX_LENGTH}
                helperText={
                  plainLength > MESSAGE_MAX_LENGTH
                    ? `Limite de ${MESSAGE_MAX_LENGTH} caracteres excedido.`
                    : `${plainLength.toLocaleString("pt-BR")} / ${MESSAGE_MAX_LENGTH.toLocaleString("pt-BR")} caracteres`
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 1 }}>
                      <Tooltip title="Ampliar editor">
                        <IconButton
                          size="small"
                          onClick={() => setExpandedEditorOpen(true)}
                          aria-label="Ampliar editor"
                          edge="end"
                        >
                          <OpenInFullIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderStyle: "dashed",
              }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>
                Dicas para um atendimento mais rápido
              </Typography>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.25 }}>
                {TIPS.map((tip) => (
                  <Typography key={tip} component="li" variant="body2" color="text.secondary">
                    {tip}
                  </Typography>
                ))}
              </Stack>
            </Paper>

            {error ? (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}

            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(`/portal-cliente/app${q}`)}
                disabled={busy}
                sx={{ textTransform: "none" }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={busy || !canSubmit}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  minHeight: 44,
                }}
              >
                {busy ? "Abrindo…" : "Abrir solicitação"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <InternalNoteExpandedEditor
        open={expandedEditorOpen}
        onClose={() => setExpandedEditorOpen(false)}
        message={message}
        setMessage={setMessage}
        onSend={() => {
          if (!canSubmit || busy) return;
          handleSubmit();
        }}
        showAttachButton={false}
        title="Descrever solicitação"
        hint="Use o editor ampliado para textos longos, listas e formatação. Após abrir a solicitação, você também poderá enviar anexos na conversa."
        sendLabel="Abrir solicitação"
        cancelLabel="Voltar"
        closeAriaLabel="Fechar editor"
      />
    </Box>
  );
}
